import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles,
  AlertTriangle,
  ShieldCheck,
  Loader2,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Lock,
  Mail,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNLOCK_STORAGE_KEY = "agentflow_risk_unlock_email";

const EXAMPLES = [
  {
    label: "Privacy clause",
    text:
      "We may share your personal information with third-party advertisers and data brokers without your consent for marketing purposes. By using our service, you irrevocably waive all rights to data deletion.",
  },
  {
    label: "Auto-renew terms",
    text:
      "Your subscription automatically renews each month at our then-current rate. Cancellations are processed within 90 days and prorated refunds are not available under any circumstance.",
  },
  {
    label: "Cookie policy",
    text:
      "We use cookies and similar tracking technologies to personalize your experience. By continuing to browse this site, you consent to all categories of cookies including third-party advertising trackers.",
  },
];

const LEVEL_THEME = {
  low: {
    ring: "ring-emerald-200",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    bar: "from-emerald-400 to-emerald-600",
    dot: "bg-emerald-500",
    title: "Looks Solid",
    icon: <ShieldCheck className="h-5 w-5" />,
  },
  medium: {
    ring: "ring-amber-200",
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    bar: "from-amber-400 to-amber-500",
    dot: "bg-amber-500",
    title: "Some Concerns",
    icon: <AlertTriangle className="h-5 w-5" />,
  },
  high: {
    ring: "ring-orange-200",
    badge: "bg-orange-50 text-orange-800 border-orange-200",
    bar: "from-orange-400 to-orange-600",
    dot: "bg-orange-500",
    title: "High Risk",
    icon: <AlertTriangle className="h-5 w-5" />,
  },
  critical: {
    ring: "ring-rose-200",
    badge: "bg-rose-50 text-rose-700 border-rose-200",
    bar: "from-rose-500 to-rose-700",
    dot: "bg-rose-600",
    title: "Critical Exposure",
    icon: <AlertTriangle className="h-5 w-5" />,
  },
};

const SEVERITY_PILL = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-orange-50 text-orange-800 border-orange-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function RiskScoreTool({ onJoinWaitlist }) {
  const [clause, setClause] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [unlockEmail, setUnlockEmail] = useState("");

  // Restore prior unlock from localStorage so repeat scans skip the gate
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(UNLOCK_STORAGE_KEY);
      if (saved) setUnlockEmail(saved);
    } catch {
      /* ignore */
    }
  }, []);

  const charCount = clause.length;
  const valid = clause.trim().length >= 20 && clause.trim().length <= 4000;

  const runAnalysis = async (emailOverride) => {
    if (!valid || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const payload = { clause: clause.trim() };
      const emailToUse = emailOverride ?? unlockEmail;
      if (emailToUse && /\S+@\S+\.\S+/.test(emailToUse)) {
        payload.email = emailToUse.toLowerCase();
      }
      const { data } = await axios.post(`${API}/risk-score`, payload);
      setResult(data);
      if (!data.gated && payload.email) {
        try {
          window.localStorage.setItem(UNLOCK_STORAGE_KEY, payload.email);
        } catch {
          /* ignore */
        }
        setUnlockEmail(payload.email);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Rate-limit responses return `detail` as an object {code, message};
      // standard 4xx/5xx return it as a string. Handle both shapes.
      const description =
        typeof detail === "string"
          ? detail
          : typeof detail?.message === "string"
          ? detail.message
          : "Please try again in a moment.";
      const isRateLimit = err?.response?.status === 429;
      toast.error(
        isRateLimit ? "Free-tier limit reached" : "Couldn't analyze clause",
        { description },
      );
    } finally {
      setLoading(false);
    }
  };

  const analyze = () => runAnalysis();

  const handleUnlock = async (email) => {
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setUnlockEmail(email);
    toast.success("Unlocking full report…");
    await runAnalysis(email);
  };

  const loadExample = (text) => {
    setClause(text);
    setResult(null);
  };

  const reset = () => {
    setResult(null);
    setClause("");
  };

  return (
    <section
      id="risk-tool"
      className="relative py-24 sm:py-32 bg-gradient-to-b from-white via-blue-50/30 to-white border-y border-slate-200"
      data-testid="risk-tool-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
              <Sparkles className="h-3 w-3" />
              <span className="uppercase tracking-[0.18em] text-[10px]">
                Free AI Tool · No signup
              </span>
            </div>
            <h2 className="mt-5 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Score any clause for{" "}
              <span className="text-blue-600">compliance risk</span> in 8 seconds.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Paste a clause from your privacy policy, terms, or contract. Our AI
              agent grades it against GDPR, CCPA, SOC2 and FTC rules — and tells
              you exactly what to fix.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "No login, no credit card",
                "Powered by Claude Sonnet 4.5",
                "Plain-language fixes a non-lawyer can act on",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white shrink-0">
                    <CheckCircle2 className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: tool */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-lg shadow-blue-900/5 overflow-hidden">
              {/* Editor */}
              <div className="p-6 sm:p-7 border-b border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <label
                    htmlFor="risk-clause"
                    className="text-sm font-semibold text-slate-900"
                  >
                    Paste a clause to analyze
                  </label>
                  <span
                    className={`text-xs ${
                      charCount > 4000 ? "text-rose-600" : "text-slate-500"
                    }`}
                    data-testid="risk-char-count"
                  >
                    {charCount}/4000
                  </span>
                </div>
                <Textarea
                  id="risk-clause"
                  value={clause}
                  onChange={(e) => setClause(e.target.value)}
                  placeholder="e.g. We may share your personal information with third-party advertisers without notice…"
                  className="min-h-[140px] resize-y rounded-lg border-slate-300 focus-visible:ring-blue-600 text-sm leading-relaxed"
                  data-testid="risk-clause-textarea"
                  maxLength={4000}
                />

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500 mr-1">Try:</span>
                  {EXAMPLES.map((ex) => (
                    <button
                      key={ex.label}
                      onClick={() => loadExample(ex.text)}
                      className="text-xs rounded-full border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 px-3 py-1.5 transition-colors text-slate-600 font-medium"
                      data-testid={`risk-example-${ex.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {ex.label}
                    </button>
                  ))}
                </div>

                <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button
                    onClick={analyze}
                    disabled={!valid || loading}
                    className="h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold sm:px-6 disabled:opacity-60"
                    data-testid="risk-analyze-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing…
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Run AI Risk Score
                      </>
                    )}
                  </Button>
                  {result && (
                    <Button
                      variant="ghost"
                      onClick={reset}
                      className="h-11 rounded-lg text-sm text-slate-600 hover:text-slate-900"
                      data-testid="risk-reset-button"
                    >
                      Reset
                    </Button>
                  )}
                  <p className="text-xs text-slate-500 sm:ml-auto">
                    We don't store full clauses — only a 200-char preview.
                  </p>
                </div>
              </div>

              {/* Loading skeleton */}
              {loading && (
                <div
                  className="p-6 sm:p-7 space-y-4 animate-pulse"
                  data-testid="risk-loading"
                >
                  <div className="h-6 w-40 bg-slate-100 rounded" />
                  <div className="h-3 w-full bg-slate-100 rounded" />
                  <div className="h-3 w-5/6 bg-slate-100 rounded" />
                  <div className="h-20 w-full bg-slate-100 rounded" />
                </div>
              )}

              {/* Result */}
              {result && !loading && (
                <RiskResult
                  result={result}
                  onJoinWaitlist={onJoinWaitlist}
                  onUnlock={handleUnlock}
                />
              )}

              {/* Empty state */}
              {!loading && !result && (
                <div className="p-6 sm:p-7 flex items-start gap-4 bg-slate-50/50">
                  <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      Your AI risk report will appear here
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Paste a clause and hit{" "}
                      <span className="font-medium text-slate-700">
                        Run AI Risk Score
                      </span>{" "}
                      — takes about 8 seconds.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RiskResult({ result, onJoinWaitlist, onUnlock }) {
  const theme = LEVEL_THEME[result.level] || LEVEL_THEME.medium;
  return (
    <div className="p-6 sm:p-7" data-testid="risk-result">
      <div className={`flex items-start gap-5 rounded-xl border bg-white p-5 ring-1 ${theme.ring}`}>
        {/* Score dial */}
        <div className="shrink-0">
          <div className="relative h-24 w-24">
            <svg className="h-24 w-24 -rotate-90" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="42"
                stroke="rgb(241 245 249)"
                strokeWidth="10"
                fill="none"
              />
              <circle
                cx="48"
                cy="48"
                r="42"
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                strokeDasharray={`${(result.score / 100) * 263.9} 263.9`}
                strokeLinecap="round"
                className={
                  result.level === "low"
                    ? "text-emerald-500"
                    : result.level === "medium"
                    ? "text-amber-500"
                    : result.level === "high"
                    ? "text-orange-500"
                    : "text-rose-600"
                }
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-display font-semibold text-2xl text-slate-900 leading-none">
                {result.score}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                / 100
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${theme.badge}`}
              data-testid="risk-level-badge"
            >
              {theme.icon}
              {theme.title}
            </span>
            {result.regulations?.map((r) => (
              <span
                key={r}
                className="inline-flex rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium px-2.5 py-1 border border-slate-200"
              >
                {r}
              </span>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">
            {result.summary}
          </p>
        </div>
      </div>

      {/* Risks */}
      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
            Risks identified
          </h4>
          {result.gated && result.total_risks > result.risks.length && (
            <span
              className="text-xs text-slate-500"
              data-testid="risks-locked-count"
            >
              Showing 1 of {result.total_risks}
            </span>
          )}
        </div>
        <ul className="mt-3 space-y-3">
          {result.risks.map((r, i) => (
            <li
              key={i}
              className="rounded-lg border border-slate-200 bg-white p-4"
              data-testid={`risk-finding-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-medium text-slate-900 text-sm leading-snug">
                  {r.title}
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${SEVERITY_PILL[r.severity]}`}
                >
                  {r.severity}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {r.detail}
              </p>
            </li>
          ))}

          {result.gated &&
            Array.from({ length: Math.max(0, result.total_risks - result.risks.length) }).map(
              (_, i) => <LockedRiskCard key={`locked-${i}`} index={i} />
            )}
        </ul>
      </div>

      {/* Fixes */}
      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
            Recommended fixes
          </h4>
          {result.gated && result.total_fixes > result.fixes.length && (
            <span
              className="text-xs text-slate-500"
              data-testid="fixes-locked-count"
            >
              Showing 1 of {result.total_fixes}
            </span>
          )}
        </div>
        <ul className="mt-3 space-y-2">
          {result.fixes.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm text-slate-700"
              data-testid={`risk-fix-${i}`}
            >
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white shrink-0">
                <CheckCircle2 className="h-3 w-3" strokeWidth={3} />
              </span>
              <span className="leading-relaxed">{f}</span>
            </li>
          ))}
          {result.gated &&
            Array.from({ length: Math.max(0, result.total_fixes - result.fixes.length) }).map(
              (_, i) => (
                <li
                  key={`fix-locked-${i}`}
                  className="flex items-start gap-3 text-sm select-none"
                  aria-hidden="true"
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-400 shrink-0">
                    <Lock className="h-3 w-3" />
                  </span>
                  <span className="leading-relaxed flex-1">
                    <span className="inline-block h-3 w-11/12 bg-slate-200/70 rounded blur-[2px] align-middle" />
                  </span>
                </li>
              )
            )}
        </ul>
      </div>

      {/* Unlock form OR Waitlist CTA */}
      {result.gated ? (
        <UnlockForm onSubmit={onUnlock} totalRisks={result.total_risks} />
      ) : (
        <div
          className="mt-7 rounded-xl bg-slate-900 text-white p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          data-testid="risk-unlocked-cta"
        >
          <div>
            <div className="font-display text-base sm:text-lg font-semibold tracking-tight flex items-center gap-2">
              <Unlock className="h-4 w-4 text-emerald-300" />
              Full report unlocked.
            </div>
            <p className="mt-1 text-sm text-slate-300">
              Want continuous monitoring across all your documents? Create
              your free account to keep scanning.
            </p>
          </div>
          <Button
            onClick={onJoinWaitlist}
            className="rounded-lg bg-white text-slate-900 hover:bg-slate-100 h-10 px-5 text-sm font-semibold shrink-0"
            data-testid="risk-result-join-waitlist"
          >
            Get Early Access
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
        This is an AI-generated screening, not legal advice. For binding decisions,
        consult a qualified attorney in your jurisdiction.
      </p>
    </div>
  );
}

function LockedRiskCard({ index }) {
  return (
    <li
      className="relative rounded-lg border border-slate-200 bg-slate-50/60 p-4 overflow-hidden"
      data-testid={`risk-finding-locked-${index}`}
      aria-hidden="true"
    >
      <div className="absolute inset-0 backdrop-blur-[3px] bg-white/30 flex items-center justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm">
          <Lock className="h-3 w-3" />
          Locked
        </span>
      </div>
      <div className="flex items-start justify-between gap-3 opacity-60 select-none">
        <div className="space-y-2 flex-1">
          <div className="h-3 w-2/3 bg-slate-300/70 rounded" />
          <div className="h-2.5 w-full bg-slate-200/80 rounded" />
          <div className="h-2.5 w-5/6 bg-slate-200/80 rounded" />
        </div>
        <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          ???
        </span>
      </div>
    </li>
  );
}

function UnlockForm({ onSubmit, totalRisks }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const validEmail = /\S+@\S+\.\S+/.test(email);

  const submit = async (e) => {
    e.preventDefault();
    if (!validEmail || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(email.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-7 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white p-5 sm:p-6"
      data-testid="unlock-form"
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex h-10 w-10 rounded-lg bg-blue-600 text-white items-center justify-center shrink-0">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base sm:text-lg font-semibold tracking-tight text-slate-900">
            Unlock the full report —{" "}
            <span className="text-blue-600">free</span>
          </div>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            We found <strong>{totalRisks}</strong> risks. Enter your email to
            reveal all findings, fixes and regulation citations. No spam — just
            your report and an occasional product update.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11 pl-10 rounded-lg border-slate-300 bg-white focus-visible:ring-blue-600 text-sm"
                data-testid="unlock-email-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={!validEmail || submitting}
              className="h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold sm:px-6 disabled:opacity-60"
              data-testid="unlock-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unlocking…
                </>
              ) : (
                <>
                  <Unlock className="mr-2 h-4 w-4" />
                  Unlock full report
                </>
              )}
            </Button>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            By unlocking, you agree to receive occasional product emails from
            AgentFlow. Unsubscribe any time.
          </p>
        </div>
      </div>
    </form>
  );
}
